import type {
  ConversationListItem,
  ProjectDocument,
  WikiActivityEntry,
  WikiLogEntry,
} from "@esi/shared";

/** How many events What changed lists. */
export const WHAT_CHANGED_ROWS = 10;

export type WhatChangedFilter = "all" | "wiki" | "sources" | "conversations";

/** One event of the project the viewer can read. */
export type WhatChangedRow =
  /** A publication: one per published change set, or one per page from the index log. */
  | {
      kind: "wiki";
      id: string;
      at: string;
      pageId: string;
      title: string;
      /** Other pages published with it (a change set of several pages). */
      more: number;
      by: string | null;
    }
  | { kind: "source"; id: string; at: string; document: ProjectDocument }
  | {
      kind: "conversation";
      id: string;
      at: string;
      conversation: ConversationListItem;
      by: string | null;
    };

/** Publications as change sets (viewers who may see drafts): one row per published set. */
export function publicationsFromActivity(entries: readonly WikiActivityEntry[]): WhatChangedRow[] {
  const rows: WhatChangedRow[] = [];
  for (const entry of entries) {
    if (entry.status !== "published" || !entry.publishedAt) {
      continue;
    }
    // Only items the server showed the viewer: a set whose pages are all hidden is not listed.
    // A deleted page is no publication to open.
    const pages = entry.items.filter(
      (i) => i.status === "published" && i.pageId && i.op !== "delete",
    );
    const first = pages[0];
    if (!first?.pageId) {
      continue;
    }
    rows.push({
      kind: "wiki",
      id: `set:${entry.id}`,
      at: entry.publishedAt,
      pageId: first.pageId,
      title: first.title,
      more: pages.length - 1,
      by: entry.publishedByName ?? null,
    });
  }
  return rows;
}

/** Publications from the index log (already reader-filtered): one row per page version. */
export function publicationsFromLog(log: readonly WikiLogEntry[]): WhatChangedRow[] {
  return log.map((entry) => ({
    kind: "wiki" as const,
    id: `log:${entry.page.id}:${entry.version}`,
    at: entry.publishedAt,
    pageId: entry.page.id,
    title: entry.page.title,
    more: 0,
    by: entry.authorName,
  }));
}

export interface WhatChangedSources {
  publications: readonly WhatChangedRow[];
  documents: readonly ProjectDocument[];
  /** Conversations shared with the project (`filter=project`); none for guests. */
  conversations: readonly ConversationListItem[];
}

/**
 * The latest events, newest first: publications, sources added (by their creation) and
 * conversations shared with the project (by their share). A conversation whose label the viewer
 * fails comes without a title or label and is left out, silently.
 */
export function whatChangedRows(
  sources: WhatChangedSources,
  filter: WhatChangedFilter = "all",
): WhatChangedRow[] {
  const rows: WhatChangedRow[] = [];
  if (filter === "all" || filter === "wiki") {
    rows.push(...sources.publications);
  }
  if (filter === "all" || filter === "sources") {
    for (const document of sources.documents) {
      rows.push({ kind: "source", id: `doc:${document.id}`, at: document.createdAt, document });
    }
  }
  if (filter === "all" || filter === "conversations") {
    for (const conversation of sources.conversations) {
      const sharedAt = conversation.projectShare?.sharedAt;
      // A null label means the viewer fails it (its title is hidden too); a null title alone is
      // a conversation without one yet, listed as untitled.
      if (!sharedAt || conversation.label === null) {
        continue;
      }
      rows.push({
        kind: "conversation",
        id: `conv:${conversation.id}`,
        at: sharedAt,
        conversation,
        by: conversation.sharedBy?.name ?? null,
      });
    }
  }
  return rows.sort((a, b) => time(b.at) - time(a.at)).slice(0, WHAT_CHANGED_ROWS);
}

/** An ISO time as a number; an unknown one sorts last. */
export const time = (iso: string) => Date.parse(iso) || 0;
